import React from "react";
import { Frequency } from "rrule";
import styled from "styled-components";
import {
    TASK_DESCRIPTION_CHAR_LIMIT,
    TASK_TITLE_CHAR_LIMIT,
} from "@lifeat/shared/task-management/TaskManagementConstants";
import { TaskStatus } from "@lifeat/shared/task-management/TaskStatus";
import { AmplitudeEventSource } from "@analytics/Amplitude/AmplitudeEvents";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { LifeAtBodySecondary, LifeAtInputLabel } from "@common/LifeAtText/LifeAtText";
import LifeAtMultiLineTextInput from "@common/LifeAtTextInput/LifeAtMultiLineTextInput";
import { useDebouncedInput } from "@common/LifeAtTextInput/useDebouncedInput";
import TrashIconSvg from "@icons/TrashIcon.svg";
import buildCompositeKey from "@util/ReactUtil";
import { DeleteTaskEvent } from "../AmplitudeTaskManagementEvents";
import { selectedDetail$ } from "../TaskManagementGlobals";
import { ClientRecurringTask, ClientTaskItemType } from "../TaskManagementTypes";
import { ProjectAssignmentDropdown } from "./ProjectAssignmentDropdown";
import { TagAssignmentDropdown } from "./TagAssignmentDropdown";
import { TaskStatusCheckbox, TaskTitleWrapper } from "./TaskItemComponents";
import TaskSchedulingDetails from "./TaskSchedulingDetails";
import ChevronLeftIcon from "/resources/icons/ChevronLeftArrow.svg";

const TaskDetailsContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const HeaderRow = styled.div`
    display: flex;
    padding-bottom: 10px;
`;

const TaskDetailsContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 15px;
`;

const TaskDetailsContent = styled.div`
    display: flex;
    flex-direction: column;
    padding: 20px 0;
    gap: 16px;
`;

const TitleRow = styled.div`
    display: flex;
`;

const TagInputRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const DeleteIcon = styled(TrashIconSvg)`
    height: 16px;
    width: 16px;
    stroke: ${(props) => props.theme.textColorPrimary};
`;

const StyledChevronLeftIcon = styled(ChevronLeftIcon)`
    cursor: pointer;
    fill: ${(props) => props.theme.iconColor};
`;

const DEBOUNCE_DELAY_MS = 1000;

const TASK_DESCRIPTION_CHARACTER_COUNT_MESSAGE = `${TASK_DESCRIPTION_CHAR_LIMIT}/${TASK_DESCRIPTION_CHAR_LIMIT} characters`;
const TASK_TITLE_CHARACTER_COUNT_MESSAGE = `${TASK_TITLE_CHAR_LIMIT}/${TASK_TITLE_CHAR_LIMIT} characters`;

const TASK_DESCRIPTION_LABEL = "Task description";
const TASK_DESCRIPTION_PLACEHOLDER = "Add your task description";
const TASK_TAG_LABEL = "Tag";
const TASK_PROJECT_LABEL = "Project";
const DELETE_TASK_LABEL = "Delete task";
const BACK_BUTTON_LABEL = "Back";

interface TaskDetailsProps {
    readonly task: ClientTaskItemType | ClientRecurringTask;
    readonly amplitudeEventSource: AmplitudeEventSource;
    readonly taskMethods: {
        buildKey: () => string;
        updateTaskStatus: (status: TaskStatus) => Promise<void>;
        updateTaskTitle: (title: string) => Promise<void>;
        updateTaskDescription: (description: string) => Promise<void>;
        updateTaskTag: (tagId: string | null) => Promise<void>;
        updateTaskProject: (projectId: string | null) => Promise<void>;
        updateTaskDuration: (duration: number) => Promise<void>;
        updateTaskDate: (date: string) => Promise<void>;
        updateTaskTime: (time: string) => Promise<void>;
        updateTaskTimeSpent: (timeSpent: number) => Promise<void>;
        removeTaskDuration: () => Promise<void>;
        removeTaskTime: () => Promise<void>;
        removeTaskDate: () => Promise<void> | null;
        makeTaskRecur: (frequency: Frequency) => Promise<void> | null;
        deleteTask: () => Promise<void>;
        removeTaskDueDate: () => Promise<void> | null;
        updateTaskDueDate: (dueDate: string) => Promise<void> | null;
    };
    readonly events: {
        onComplete: () => void;
        onTagAssign: (tagId: string) => void;
        onTagRemove: () => void;
    };
}
export default function TaskDetails(props: TaskDetailsProps) {
    const amplitudeService = AmplitudeService.getInstance();

    const isTaskCompleted = props.task.status === "COMPLETE";

    const [titleInputVal, setTitleInputValAndDebounce] = useDebouncedInput(
        props.task.title,
        (updatedTitle: string) => props.taskMethods.updateTaskTitle(updatedTitle),
        DEBOUNCE_DELAY_MS,
    );

    const [descriptionInputVal, setDescriptionInputValAndDebounce] = useDebouncedInput(
        props.task.description || "",
        (updatedDescription: string) => props.taskMethods.updateTaskDescription(updatedDescription),
        DEBOUNCE_DELAY_MS,
    );

    const close = () => {
        selectedDetail$.value = null;
    };

    const handleDeleteTask = () => {
        close();
        props.taskMethods.deleteTask();
        amplitudeService.logEvent(new DeleteTaskEvent({ source: props.amplitudeEventSource }));
    };

    const taskSchedulingDetailsKey = buildCompositeKey(
        `task-scheduling-details`,
        props.taskMethods.buildKey(),
        `${props.task.schedulingDetails?.durationMins}_mins` ?? "no-duration",
        `${props.task.schedulingDetails?.scheduleDate}_date` ?? "no-date",
        `${props.task.schedulingDetails?.scheduleTime}_time` ?? "no-time",
    );

    return (
        <TaskDetailsContainer>
            <HeaderRow>
                <LifeAtButtonV2 variant={"ghost"} size={"small"} onClick={close} disableHover={true}>
                    <StyledChevronLeftIcon />
                    {BACK_BUTTON_LABEL}
                </LifeAtButtonV2>
            </HeaderRow>
            <TaskDetailsContentWrapper>
                <TitleRow>
                    <TaskStatusCheckbox
                        key={buildCompositeKey(`task-status-checkbox`, props.taskMethods.buildKey(), isTaskCompleted)}
                        updateTaskStatus={props.taskMethods.updateTaskStatus}
                        isTaskCompleted={isTaskCompleted}
                        onComplete={props.events.onComplete}
                    />
                    <TaskTitleWrapper isCompleted={isTaskCompleted}>
                        <LifeAtMultiLineTextInput
                            textValue={titleInputVal}
                            onInputChange={setTitleInputValAndDebounce}
                            minHeightPx={21}
                            maxHeightPx={200}
                            maxCharacterCount={TASK_TITLE_CHAR_LIMIT}
                            errorMessage={
                                titleInputVal.length >= TASK_TITLE_CHAR_LIMIT
                                    ? TASK_TITLE_CHARACTER_COUNT_MESSAGE
                                    : undefined
                            }
                        />
                    </TaskTitleWrapper>
                </TitleRow>
                <TaskDetailsContent>
                    <TagInputRow>
                        <LifeAtInputLabel>{TASK_PROJECT_LABEL}</LifeAtInputLabel>
                        <ProjectAssignmentDropdown
                            selectedProjectId={props.task.projectId ?? undefined}
                            updateTaskProject={props.taskMethods.updateTaskProject}
                        />
                    </TagInputRow>
                    <TagInputRow>
                        <LifeAtInputLabel>{TASK_TAG_LABEL}</LifeAtInputLabel>
                        <TagAssignmentDropdown
                            selectedTagId={props.task.tagId || undefined}
                            updateTaskTag={props.taskMethods.updateTaskTag}
                            onAssign={props.events.onTagAssign}
                            onRemove={props.events.onTagRemove}
                        />
                    </TagInputRow>
                    <LifeAtBodySecondary>{TASK_DESCRIPTION_LABEL}</LifeAtBodySecondary>
                    <LifeAtMultiLineTextInput
                        textValue={descriptionInputVal}
                        onInputChange={setDescriptionInputValAndDebounce}
                        minHeightPx={62}
                        maxHeightPx={200}
                        maxCharacterCount={TASK_DESCRIPTION_CHAR_LIMIT}
                        placeholder={TASK_DESCRIPTION_PLACEHOLDER}
                        errorMessage={
                            descriptionInputVal.length >= TASK_DESCRIPTION_CHAR_LIMIT
                                ? TASK_DESCRIPTION_CHARACTER_COUNT_MESSAGE
                                : undefined
                        }
                        showBorder={true}
                    />
                    <TaskSchedulingDetails
                        key={taskSchedulingDetailsKey}
                        rule={props.task.type === "recurring" ? props.task.rule : undefined}
                        schedulingDetails={props.task.schedulingDetails || undefined}
                        timeSpentMins={props.task.timeSpentMins || undefined}
                        amplitudeEventSource={props.amplitudeEventSource}
                        methods={props.taskMethods}
                    />

                    <LifeAtButtonV2 onClick={handleDeleteTask} size={"small"}>
                        <DeleteIcon />
                        {DELETE_TASK_LABEL}
                    </LifeAtButtonV2>
                </TaskDetailsContent>
            </TaskDetailsContentWrapper>
        </TaskDetailsContainer>
    );
}
